
import { Component, HostListener, inject } from '@angular/core';
import { Event as RouterEvent, Router, NavigationStart, NavigationEnd, RouterModule } from '@angular/router';
import { PageLoaderComponent } from './layout/page-loader/page-loader.component';
@Component({
  selector: 'app-root',
  imports: [
    RouterModule,
    PageLoaderComponent
  ],
  providers: [],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent {
  _router = inject(Router);

  currentUrl!: string;
  constructor() {
    this._router.events.subscribe((routerEvent: RouterEvent) => {
      if (routerEvent instanceof NavigationStart) {
        this.currentUrl = routerEvent.url.substring(
          routerEvent.url.lastIndexOf('/') + 1
        );
      }
      if (routerEvent instanceof NavigationEnd) {
        /* empty */
      }
      window.scrollTo(0, 0);
    });
  }

  @HostListener('document:input', ['$event'])
  handleGlobalTextNormalization(event: Event): void {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) {
      return;
    }

    if (!this.shouldNormalize(target)) {
      return;
    }

    let normalizedValue = target.value;

    if (this.shouldStripSpaces(target)) {
      normalizedValue = this.withoutSpaces(normalizedValue);
    }

    if (this.shouldSanitize(target)) {
      normalizedValue = this.toAscii(normalizedValue);
    }

    if (this.shouldLowercase(target)) {
      normalizedValue = normalizedValue.toLowerCase();
    } else if (this.shouldUppercase(target)) {
      normalizedValue = normalizedValue.toUpperCase();
    }

    if (normalizedValue === target.value) {
      return;
    }

    const selectionStart = target.selectionStart ?? normalizedValue.length;
    const selectionEnd = target.selectionEnd ?? normalizedValue.length;

    target.value = normalizedValue;
    target.dispatchEvent(new Event('input', { bubbles: false }));

    try {
      target.setSelectionRange(selectionStart, selectionEnd);
    } catch {
      /* ignore non-text inputs without cursor support */
    }
  }

  private shouldNormalize(target: HTMLInputElement | HTMLTextAreaElement): boolean {
    return this.shouldStripSpaces(target) ||
      this.shouldSanitize(target) ||
      this.shouldLowercase(target) ||
      this.shouldUppercase(target);
  }

  private shouldStripSpaces(target: HTMLInputElement | HTMLTextAreaElement): boolean {
    return target.dataset['noSpaces'] === 'true';
  }

  private shouldSanitize(target: HTMLInputElement | HTMLTextAreaElement): boolean {
    if (this.allowsUnicode(target)) {
      return false;
    }

    if (target instanceof HTMLTextAreaElement) {
      return true;
    }

    const type = (target.type || 'text').toLowerCase();
    return ['text', 'search', 'email', 'password', 'tel', 'url'].includes(type);
  }

  private shouldUppercase(target: HTMLInputElement | HTMLTextAreaElement): boolean {
    if (this.allowsUnicode(target) || this.shouldPreserveCase(target) || this.shouldLowercase(target)) {
      return false;
    }

    if (target instanceof HTMLTextAreaElement) {
      return true;
    }

    const type = (target.type || 'text').toLowerCase();
    return ['text', 'search', 'tel'].includes(type);
  }

  private shouldLowercase(target: HTMLInputElement | HTMLTextAreaElement): boolean {
    return target.dataset['lowercase'] === 'true';
  }

  private allowsUnicode(target: HTMLInputElement | HTMLTextAreaElement): boolean {
    const markers = [
      target.getAttribute('formcontrolname'),
      target.getAttribute('name'),
      target.getAttribute('id'),
      target.getAttribute('placeholder'),
      target.getAttribute('aria-label'),
      target.dataset['allowUnicode'],
    ]
      .filter((value): value is string => !!value)
      .map((value) => value.toLowerCase());

    return markers.some((value) =>
      value.includes('description') ||
      value.includes('descripcion') ||
      value === 'true'
    );
  }

  private shouldPreserveCase(target: HTMLInputElement | HTMLTextAreaElement): boolean {
    const markers = [
      target.getAttribute('formcontrolname'),
      target.getAttribute('name'),
      target.getAttribute('id'),
      target.getAttribute('placeholder'),
      target.getAttribute('aria-label'),
      target.dataset['preserveCase'],
    ]
      .filter((value): value is string => !!value)
      .map((value) => value.toLowerCase());

    return markers.some((value) =>
      value.includes('password') ||
      value.includes('contrasena') ||
      value.includes('contraseña') ||
      value.includes('email') ||
      value.includes('correo') ||
      value.includes('uid') ||
      value.includes('uuid') ||
      value.includes('url') ||
      value === 'true'
    );
  }

  private toAscii(value: string): string {
    return value
      .replace(/ñ/g, '__enie_lower__')
      .replace(/Ñ/g, '__enie_upper__')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\x20-\x7E]/g, '')
      .replace(/__enie_lower__/g, 'ñ')
      .replace(/__enie_upper__/g, 'Ñ');
  }

  private withoutSpaces(value: string): string {
    return value.replace(/\s+/g, '');
  }
}
